// stores/userStore.ts

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Link, ReadOnlyProps, User } from '@/services/client/types';
import { useClient } from '@/composables/useClient';
import { useRouter } from 'vue-router';

interface UserState {
  users: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
}

export const useUserStore = defineStore('user', () => {
  const state = ref<UserState>({
    users: [],
    currentUser: null,
    loading: false,
    error: null,
  });

  const client = useClient()

  // Actions
  const fetchAllUsers = async () => {
    state.value.loading = true;
    state.value.error = null;
    try {
      const users = await client.getAllUsers();
      console.log("Setting users...", users)
      state.value.users = users
    } catch (err) {
      state.value.error = (err as Error).message;
    } finally {
      state.value.loading = false;
    }
  };

  const getUser = async (id: number): Promise<User | null> => {
    state.value.loading = true
    state.value.error = null
    try {
      const user = await client.getUser(id)
      state.value.loading = false;
      return user
    } catch (err) {
      state.value.error = (err as Error).message;
      state.value.loading = false;
      return null
    }
  }

  // const getMe = async (): Promise<User | null> => {

  // }

  const createUser = async (user: Omit<User, 'id' | ReadOnlyProps>): Promise<User | null> => {
    state.value.loading = true;
    state.value.error = null;
    try {
      const newUser = await client.createUser(user);
      state.value.users.push(newUser);
      console.warn(newUser)
      return newUser;
    } catch (err) {
      state.value.error = (err as Error).message;
    } finally {
      state.value.loading = false;
    }
    return null
  };

  const updateUser = async (user: Omit<User, ReadOnlyProps>) => {
    state.value.loading = true;
    state.value.error = null;
    try {
      const updatedUser = await client.updateUser(user);
      const index = state.value.users.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        state.value.users[index] = updatedUser;
      }
      if (state.value.currentUser?.id === updatedUser.id) {
        state.value.currentUser = updatedUser;
      }
    } catch (err) {
      state.value.error = (err as Error).message;
    } finally {
      state.value.loading = false;
    }
  };

  const deleteUser = async (user: Omit<User, ReadOnlyProps>) => {
    state.value.loading = true;
    state.value.error = null;
    try {
      const deletedUser = await client.deleteUser(user);
      const index = state.value.users.findIndex(u => u.id === deletedUser.id);
      if (index !== -1) {
        delete state.value.users[index];
      }
      if (state.value.currentUser?.id === deletedUser.id) {
        console.warn(`Deleted currently logged in user. Triggering logout...`)
        const router = useRouter()
        router.push('/logout')
      }
    } catch (err) {
      state.value.error = (err as Error).message;
    } finally {
      state.value.loading = false;
    }
  };

  const deleteLinks = async (user: Omit<User, ReadOnlyProps>) => {
    state.value.loading = true;
    state.value.error = null;
    try {
      const updatedUser = await client.deleteUserLinks(user);
      const index = state.value.users.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        state.value.users[index] = updatedUser;
      }
      if (state.value.currentUser?.id === updatedUser.id) {
        state.value.currentUser = updatedUser;
      }
    } catch (err) {
      state.value.error = (err as Error).message;
    } finally {
      state.value.loading = false;
    }
  }

  const setCurrentUser = (user: User) => {
    state.value.currentUser = user;
  };

  const clearUsers = () => {
    state.value.users = [];
    state.value.currentUser = null;
  };

  return {
    // Return refs directly instead of spreading state.value
    users: computed(() => state.value.users),
    currentUser: computed(() => state.value.currentUser),
    loading: computed(() => state.value.loading),
    error: computed(() => state.value.error),

    // Actions
    getUser,
    fetchAllUsers,
    createUser,
    updateUser,
    deleteUser,
    deleteLinks,
    setCurrentUser,
    clearUsers,
  };
}, {
  persist: {
    key: 'user',
    storage: localStorage, // Or sessionStorage
  },
});
